import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Paper,
    Button,
    Alert
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Reservation } from '../../types';

const formatReservationStatus = (status?: string) => {
    const map: Record<string, string> = {
        confirmed: 'Confirmée',
        pending: 'En attente',
        cancelled: 'Annulée',
        completed: 'Terminée',
    };
    return status ? (map[status] || status) : '—';
};

const formatPaymentStatus = (status?: string) => {
    const map: Record<string, string> = {
        paid: 'Payé',
        unpaid: 'Non payé',
        pending: 'En attente',
        failed: 'Échec',
        refunded: 'Remboursé',
    };
    return status ? (map[status] || status) : '—';
};

const formatPaymentMethod = (method?: string) => {
    const map: Record<string, string> = {
        card: 'Carte bancaire',
        cash: 'Espèces',
        transfer: 'Virement',
        paypal: 'PayPal',
    };
    return method ? (map[method] || method) : '—';
};

const formatTimeSlot = (timeSlot?: { startTime?: string; endTime?: string }) => {
    if (!timeSlot?.startTime) return '—';
    if (!timeSlot?.endTime || timeSlot.endTime === timeSlot.startTime) return timeSlot.startTime;
    return `${timeSlot.startTime} - ${timeSlot.endTime}`;
};

const getActivityLabel = (reservation: any) => {
    // Si le backend renvoie un product "populated"
    if (reservation?.product && typeof reservation.product === 'object') {
        return reservation.product?.name || '—';
    }
    // Si c’est juste un ID string, on n’affiche pas
    return '—';
};

const PaymentSuccess: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // Vérification de location.state et valeurs par défaut
    const state = (location.state || {}) as { message?: string; reservation?: Reservation };
    const { message = 'Paiement réussi !', reservation } = state;

    if (!reservation) {
        return (
            <Container maxWidth="md" sx={{ mt: 4 }}>
                <Alert severity="error">
                    Aucune réservation trouvée. Veuillez contacter le support si vous pensez qu'il s'agit d'une erreur.
                </Alert>
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate('/')}
                    >
                        Retour à l'accueil
                    </Button>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h4" component="h1" gutterBottom>
                    {message}
                </Typography>

                <Box sx={{ mt: 4, mb: 4 }}>
                    <Typography variant="h6" gutterBottom>
                        Détails de la réservation
                    </Typography>

                    <Typography>
                        Date: {reservation?.date ? new Date(reservation.date as any).toLocaleDateString('fr-FR') : '—'}
                    </Typography>

                    <Typography>
                        Heure: {formatTimeSlot((reservation as any)?.timeSlot)}
                    </Typography>


                    <Typography>
                        Statut: {formatReservationStatus((reservation as any)?.status)}
                    </Typography>

                    <Typography>
                        Client: {(reservation as any)?.customer?.name || '—'}
                    </Typography>

                    <Typography>
                        Email: {(reservation as any)?.customer?.email || '—'}
                    </Typography>

                    <Typography>
                        Téléphone: {(reservation as any)?.customer?.phone || '—'}
                    </Typography>

                    <Typography sx={{ mt: 1 }}>
                        Paiement: {(reservation as any)?.payment?.status
                            ? `${formatPaymentStatus((reservation as any).payment.status)} — ${(reservation as any).payment.amount ?? '—'}€ (${formatPaymentMethod((reservation as any).payment.method)})`
                            : '—'}
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/')}
                >
                    Retour à l'accueil
                </Button>
            </Paper>
        </Container>
    );
};

export default PaymentSuccess;
